// supabase/functions/_shared/gavion/goals.ts
// GAVION X4: moved from src/components/gavion/domain/goals.ts (re-exported there) so the daily digest
// (gavion-digest) reads the same targets as the app.
// Daily goals (X1). Stored in configuration.config.gavion.goals — readable by every active user through the existing
// configuration RLS, written by admins (Paramètres → Objectifs). No SQL function interprets the targets
// (gavion-digest reads the raw object and resolves it here), so gavion_config()'s defaults stay unchanged.

export const GOAL_KEYS = ["dials", "conversations", "demos"] as const;
export type GoalKey = (typeof GOAL_KEYS)[number];
export type GoalTargets = Record<GoalKey, number>;
export type GoalsConfig = {
  defaults: GoalTargets;
  /** Keyed by sales.id (as a string, JSON object key); only the keys a rep overrides. */
  per_rep: Record<string, Partial<GoalTargets>>;
};

export const MAX_GOAL = 1000;
export const DEFAULT_GOAL_TARGETS: GoalTargets = Object.freeze({
  dials: 60,
  conversations: 10,
  demos: 1,
});

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const validTarget = (v: unknown): v is number =>
  typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= MAX_GOAL;

const pickTargets = (raw: unknown): Partial<GoalTargets> => {
  if (!isObject(raw)) return {};
  const out: Partial<GoalTargets> = {};
  for (const k of GOAL_KEYS) if (validTarget(raw[k])) out[k] = raw[k];
  return out;
};

/** Tolerant reader: invalid values are ignored key by key, never thrown. */
export function normalizeGoals(raw: unknown): GoalsConfig {
  const src = isObject(raw) ? raw : {};
  const per_rep: GoalsConfig["per_rep"] = {};
  if (isObject(src.per_rep))
    for (const [id, v] of Object.entries(src.per_rep)) {
      if (!/^\d+$/.test(id)) continue;
      const t = pickTargets(v);
      if (Object.keys(t).length) per_rep[id] = t;
    }
  return {
    defaults: { ...DEFAULT_GOAL_TARGETS, ...pickTargets(src.defaults) },
    per_rep,
  };
}

/** Today's targets for one rep: defaults, then that rep's overrides. */
export function resolveGoals(raw: unknown, salesId: number): GoalTargets {
  const g = normalizeGoals(raw);
  return { ...g.defaults, ...g.per_rep[String(salesId)] };
}

/** A target of 0 means "no goal" for that metric. */
export const goalProgress = (count: number, target: number) =>
  target > 0
    ? {
        percent: Math.min(100, Math.round((count / target) * 100)),
        hit: count >= target,
      }
    : { percent: 0, hit: false };
